import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { referralAPI } from '../utils/api';
import { X, UserPlus, Plus, Trash2, ChevronDown, ChevronUp, DollarSign } from 'lucide-react';
import { toast } from 'sonner';

const ReferMerchant = ({ onClose, onSuccess }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [agreements, setAgreements] = useState([]);
  const [expandedMids, setExpandedMids] = useState([0]); // Track which MID sections are expanded
  const [merchantMids, setMerchantMids] = useState([
    { mid_number: '', website_url: '', processor_name: '', held_reserves_amount: '', notes: '' }
  ]);
  const [formData, setFormData] = useState({
    referral_agreement_id: '',
    merchant_signer_name: '',
    merchant_url: '',
    merchant_contact_name: '',
    merchant_contact_email: '',
    merchant_contact_phone: '',
    merchant_contact_address: '',
    merchant_bank_name: '',
    merchant_bank_address: '',
    merchant_bank_phone: '',
    merchant_bank_previous_contact: '',
    processor_name: '',
    mid_application_reference: '',
    merchant_company_name: ''
  });

  useEffect(() => {
    loadAgreements();
  }, []);

  const loadAgreements = async () => {
    try {
      const response = await referralAPI.getAgreements();
      setAgreements(response.data);
      if (response.data.length > 0) {
        setFormData(prev => ({ ...prev, referral_agreement_id: response.data[0].id }));
      }
    } catch (error) {
      console.error('Failed to load agreements:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleMidChange = (index, field, value) => {
    const updatedMids = [...merchantMids];
    updatedMids[index][field] = value;
    setMerchantMids(updatedMids);
  };

  const addMidEntry = () => {
    const newIndex = merchantMids.length;
    setMerchantMids([...merchantMids, { 
      mid_number: '', 
      website_url: '', 
      processor_name: '', 
      held_reserves_amount: '', 
      notes: '' 
    }]);
    setExpandedMids([...expandedMids, newIndex]); // Auto-expand new entry
  };

  const removeMidEntry = (index) => {
    if (merchantMids.length > 1) {
      const updatedMids = merchantMids.filter((_, i) => i !== index);
      setMerchantMids(updatedMids);
      setExpandedMids(expandedMids.filter(i => i !== index).map(i => i > index ? i - 1 : i));
    }
  };

  const toggleMidExpansion = (index) => {
    if (expandedMids.includes(index)) {
      setExpandedMids(expandedMids.filter(i => i !== index));
    } else {
      setExpandedMids([...expandedMids, index]);
    }
  };

  const calculateTotalReserves = () => {
    return merchantMids.reduce((total, mid) => {
      return total + (parseFloat(mid.held_reserves_amount) || 0);
    }, 0);
  };

  const getMidSummary = (mid, index) => {
    const amount = parseFloat(mid.held_reserves_amount) || 0;
    const processor = mid.processor_name || 'No processor';
    const midNum = mid.mid_number || `MID #${index + 1}`;
    return { amount, processor, midNum };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (agreements.length === 0) {
      toast.error('Please create a referral agreement first');
      return;
    }

    // Validate at least one MID with amount
    const validMids = merchantMids.filter(mid => 
      mid.mid_number && mid.processor_name && parseFloat(mid.held_reserves_amount) > 0
    );
    
    if (validMids.length === 0) {
      toast.error('Please add at least one MID with processor and amount');
      return;
    }
    
    setLoading(true);

    try {
      const totalAmount = calculateTotalReserves();
      
      // Format MIDs for submission
      const formattedMids = validMids.map(mid => ({
        mid_number: mid.mid_number,
        website_url: mid.website_url || '',
        processor_name: mid.processor_name,
        held_reserves_amount: parseFloat(mid.held_reserves_amount) || 0,
        notes: mid.notes || ''
      }));

      const submissionData = {
        ...formData,
        held_reserves_amount: totalAmount,
        merchant_mids: formattedMids
      };
      
      await referralAPI.submitMerchant(submissionData);
      toast.success('Merchant referral submitted successfully!');
      if (onSuccess) onSuccess();
      if (onClose) onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit merchant referral');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[95vh] overflow-y-auto my-8">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-200 p-6 flex justify-between items-center z-10">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Refer a Merchant</h2>
            <p className="text-sm text-slate-600 mt-1">Submit a merchant with frozen reserves</p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600"
            data-testid="close-refer-btn"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {agreements.length === 0 ? (
            <div className="text-center py-8">
              <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <UserPlus className="text-amber-600" size={40} />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">No Active Referral Agreement</h3>
              <p className="text-slate-600 mb-6">Please create a referral agreement before submitting merchant referrals.</p>
              <button
                onClick={onClose}
                className="primary-button"
              >
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="space-y-6">
                {/* Agreement Selection */}
                {agreements.length > 1 && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Referral Agreement *</label>
                    <select
                      name="referral_agreement_id"
                      value={formData.referral_agreement_id}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                      required
                    >
                      {agreements.map(agreement => (
                        <option key={agreement.id} value={agreement.id}>
                          {agreement.referrer_name} - {new Date(agreement.effective_date).toLocaleDateString()}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Merchant Information */}
                <div>
                  <h3 className="font-semibold text-slate-900 mb-3">Merchant Information</h3>
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Company Name *</label>
                        <input
                          type="text"
                          name="merchant_company_name"
                          value={formData.merchant_company_name}
                          onChange={handleChange}
                          placeholder="Merchant's business name"
                          className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Signer Name *</label>
                        <input
                          type="text"
                          name="merchant_signer_name"
                          value={formData.merchant_signer_name}
                          onChange={handleChange}
                          placeholder="Legal signer name"
                          className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Merchant Website URL</label>
                      <input
                        type="url"
                        name="merchant_url"
                        value={formData.merchant_url}
                        onChange={handleChange}
                        placeholder="https://example.com"
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                      />
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div>
                  <h3 className="font-semibold text-slate-900 mb-3">Contact Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Contact Name *</label>
                      <input
                        type="text"
                        name="merchant_contact_name"
                        value={formData.merchant_contact_name}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Contact Email *</label>
                      <input
                        type="email"
                        name="merchant_contact_email"
                        value={formData.merchant_contact_email}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Contact Phone *</label>
                      <input
                        type="tel"
                        name="merchant_contact_phone"
                        value={formData.merchant_contact_phone}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Contact Address *</label>
                      <input
                        type="text"
                        name="merchant_contact_address"
                        value={formData.merchant_contact_address}
                        onChange={handleChange}
                        placeholder="Full business address"
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Bank Information */}
                <div>
                  <h3 className="font-semibold text-slate-900 mb-3">Merchant Bank Information</h3>
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Bank Name *</label>
                        <input
                          type="text"
                          name="merchant_bank_name"
                          value={formData.merchant_bank_name}
                          onChange={handleChange}
                          placeholder="e.g., Chase Bank, Wells Fargo"
                          className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Bank Phone *</label>
                        <input
                          type="tel"
                          name="merchant_bank_phone"
                          value={formData.merchant_bank_phone}
                          onChange={handleChange}
                          className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Bank Address *</label>
                      <input
                        type="text"
                        name="merchant_bank_address"
                        value={formData.merchant_bank_address}
                        onChange={handleChange}
                        placeholder="Full address of the bank branch"
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Previous Bank Contact (if any)</label>
                      <input
                        type="text"
                        name="merchant_bank_previous_contact"
                        value={formData.merchant_bank_previous_contact}
                        onChange={handleChange}
                        placeholder="Previous representative contacted"
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                      />
                    </div>
                  </div>
                </div>

                {/* Processor Info */}
                <div>
                  <h3 className="font-semibold text-slate-900 mb-3">Primary Processor Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Processor Name *</label>
                      <input
                        type="text"
                        name="processor_name"
                        value={formData.processor_name}
                        onChange={handleChange}
                        placeholder="e.g., Stripe, Square, PayPal"
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Application/Reference #</label>
                      <input
                        type="text"
                        name="mid_application_reference"
                        value={formData.mid_application_reference}
                        onChange={handleChange}
                        placeholder="MID application reference"
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                      />
                    </div>
                  </div>
                </div>

                {/* Multiple MIDs Section - Expandable */}
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="font-semibold text-slate-900">
                      MID Accounts & Frozen Reserves
                      <span className="ml-2 text-sm font-normal text-slate-500">
                        ({merchantMids.length} {merchantMids.length === 1 ? 'account' : 'accounts'})
                      </span>
                    </h3>
                    <button
                      type="button"
                      onClick={addMidEntry}
                      className="flex items-center gap-1 text-purple-600 hover:text-purple-700 text-sm font-medium"
                    >
                      <Plus size={16} />
                      Add MID Account
                    </button>
                  </div>

                  {/* Total Summary Card */}
                  <div className="bg-gradient-to-r from-purple-50 to-cyan-50 border border-purple-200 rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-purple-600 flex items-center justify-center">
                          <DollarSign className="text-white" size={20} />
                        </div>
                        <div>
                          <div className="text-sm text-slate-600">Total Held Reserves</div>
                          <div className="text-2xl font-bold text-purple-700">
                            ${calculateTotalReserves().toLocaleString()}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-slate-500">Your Commission (10%)</div>
                        <div className="text-lg font-semibold text-green-600">
                          ${(calculateTotalReserves() * 0.1).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* MID Entries - Expandable Sections */}
                  <div className="space-y-3">
                    {merchantMids.map((mid, index) => {
                      const { amount, processor, midNum } = getMidSummary(mid, index);
                      const isExpanded = expandedMids.includes(index);
                      
                      return (
                        <div
                          key={index}
                          className={`border rounded-lg overflow-hidden transition-all ${
                            isExpanded ? 'border-purple-300 bg-purple-50/30' : 'border-slate-200 bg-white'
                          }`}
                        >
                          {/* Collapsed Header */}
                          <div
                            className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50"
                            onClick={() => toggleMidExpansion(index)}
                          >
                            <div className="flex items-center gap-4">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                                amount > 0 ? 'bg-purple-600 text-white' : 'bg-slate-200 text-slate-600'
                              }`}>
                                {index + 1}
                              </div>
                              <div>
                                <div className="font-medium text-slate-900">
                                  {mid.mid_number || `MID Account #${index + 1}`}
                                </div>
                                <div className="text-sm text-slate-500">
                                  {processor} • ${amount.toLocaleString()}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {merchantMids.length > 1 && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeMidEntry(index);
                                  }}
                                  className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                                >
                                  <Trash2 size={16} />
                                </button>
                              )}
                              {isExpanded ? (
                                <ChevronUp size={20} className="text-slate-400" />
                              ) : (
                                <ChevronDown size={20} className="text-slate-400" />
                              )}
                            </div>
                          </div>

                          {/* Expanded Content */}
                          {isExpanded && (
                            <div className="px-4 pb-4 pt-2 border-t border-slate-200 bg-white">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-sm font-medium text-slate-700 mb-2">
                                    MID Number / Reference *
                                  </label>
                                  <input
                                    type="text"
                                    value={mid.mid_number}
                                    onChange={(e) => handleMidChange(index, 'mid_number', e.target.value)}
                                    placeholder="e.g., MID-12345678"
                                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-slate-700 mb-2">
                                    Website URL for this MID
                                  </label>
                                  <input
                                    type="url"
                                    value={mid.website_url}
                                    onChange={(e) => handleMidChange(index, 'website_url', e.target.value)}
                                    placeholder="https://example.com"
                                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-slate-700 mb-2">
                                    Processor Name *
                                  </label>
                                  <input
                                    type="text"
                                    value={mid.processor_name}
                                    onChange={(e) => handleMidChange(index, 'processor_name', e.target.value)}
                                    placeholder="e.g., Stripe, Square, Adyen"
                                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-slate-700 mb-2">
                                    Held Reserves Amount ($) *
                                  </label>
                                  <input
                                    type="number"
                                    value={mid.held_reserves_amount}
                                    onChange={(e) => handleMidChange(index, 'held_reserves_amount', e.target.value)}
                                    min="0"
                                    step="0.01"
                                    placeholder="0.00"
                                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                                  />
                                </div>
                                <div className="md:col-span-2">
                                  <label className="block text-sm font-medium text-slate-700 mb-2">
                                    Additional Notes
                                  </label>
                                  <textarea
                                    value={mid.notes}
                                    onChange={(e) => handleMidChange(index, 'notes', e.target.value)}
                                    placeholder="Any additional information about this MID account..."
                                    rows={2}
                                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none resize-none"
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <p className="text-xs text-slate-500 mt-3">
                    Add all MID accounts with frozen reserves. Each MID will be tracked separately for recovery.
                  </p>
                </div>

              </div>

              <div className="flex gap-4 pt-6 mt-6 border-t border-slate-200">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-6 py-3 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 transition-colors"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 primary-button py-3 disabled:opacity-50"
                  disabled={loading}
                  data-testid="submit-referral-btn"
                >
                  {loading ? 'Submitting...' : 'Submit Referral'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReferMerchant;
